import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CheckCircle } from 'lucide-react';
import { Button } from '~/components/ui/buttons/Button';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import { useConfirmShipmentReceipt } from '~/api';
import { extractGraphQLErrorMessages, formatDate } from '~/utils';
import { PackingListModal } from '~/pages/order-view/sections/PackingListModal';
import type { OrderShipmentForBuyer } from '~/api/order';

type ShipmentsSectionProps = Readonly<{
  shipments: OrderShipmentForBuyer[];
  currency?: string;
}>;

/**
 * End-buyer view of an order's shipped/delivered shipments. Each shipment is shown as a
 * neutral, sequentially-labeled card (no seller identity) with its items, dates, a link to
 * view the shipment's packing list, and — while SHIPPED — a per-shipment receipt confirmation.
 */
export function ShipmentsSection({
  shipments,
  currency,
}: ShipmentsSectionProps) {
  const { t } = useTranslation('order');
  const [modalShipmentId, setModalShipmentId] = useState<string | null>(null);
  const [confirmingShipmentId, setConfirmingShipmentId] = useState<
    string | null
  >(null);
  const { mutate: confirmShipmentReceipt, isPending } =
    useConfirmShipmentReceipt();

  if (shipments.length === 0) return null;

  const modalShipmentIndex = shipments.findIndex(
    (shipment) => shipment.id === modalShipmentId
  );
  const modalShipment =
    modalShipmentIndex >= 0 ? shipments[modalShipmentIndex] : null;

  const handleConfirmReceipt = (shipmentId: string) => {
    if (isPending) return;
    setConfirmingShipmentId(shipmentId);
    confirmShipmentReceipt(
      { subOrderId: shipmentId },
      {
        onSuccess: () => {
          setConfirmingShipmentId(null);
          toast.success(t('order.shipments.confirm_receipt_success'));
        },
        onError: (error) => {
          setConfirmingShipmentId(null);
          toast.error(
            t('order.shipments.confirm_receipt_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          );
        },
      }
    );
  };

  return (
    <div className="bg-neut-0 p-6 rounded-lg flex flex-col gap-4 border border-neut-200 shadow-sm">
      <h2 className="text-neut-prim font-semibold">
        {t('order.shipments.heading')}
      </h2>
      <div className="flex flex-col gap-3">
        {shipments.map((shipment, index) => {
          const shipmentLabel = t('order.shipments.shipment_label', {
            index: index + 1,
          });
          const dateLabel = shipment.receivedDate
            ? t('order.shipments.received_on', {
                date: formatDate(shipment.receivedDate),
              })
            : shipment.shippedDate
              ? t('order.shipments.shipped_on', {
                  date: formatDate(shipment.shippedDate),
                })
              : null;
          const isConfirmingThisShipment =
            isPending && confirmingShipmentId === shipment.id;

          return (
            <div
              key={shipment.id}
              className="border border-neut-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-neut-prim">
                    {shipmentLabel}
                  </p>
                  {dateLabel && (
                    <p className="text-xs text-neut-sec mt-0.5">{dateLabel}</p>
                  )}
                </div>
                <OrderStatusBadge status={shipment.status} size="caption" />
              </div>

              <div className="flex flex-col divide-y divide-neut-100 border-t border-b border-neut-100 mb-3">
                {shipment.items.map((item) => (
                  <div
                    key={item.platformProductsLengths.id}
                    className="flex items-center justify-between py-2 text-sm"
                  >
                    <span className="text-neut-prim font-medium flex items-center gap-2">
                      {item.platformProductsLengths.platformProduct.name}
                      <span className="px-2 py-1 bg-neut-100 rounded-md text-xs font-normal">
                        {item.platformProductsLengths.lengthDisplayValue}
                      </span>
                    </span>
                    <span className="text-neut-sec">
                      {item.quantity.value} {item.quantity.unit}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button
                  size="sm"
                  width="auto"
                  variant="secondary"
                  onClick={() => setModalShipmentId(shipment.id)}
                >
                  {t('order.shipments.view_packing_list_button')}
                </Button>
                {shipment.status === 'SHIPPED' && (
                  <Button
                    size="sm"
                    width="auto"
                    onClick={() => handleConfirmReceipt(shipment.id)}
                    disabled={isPending}
                  >
                    <CheckCircle size={16} aria-hidden="true" />
                    {isConfirmingThisShipment
                      ? t('order.shipments.confirm_receipt_confirming')
                      : t('order.shipments.confirm_receipt_button')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modalShipment && (
        <PackingListModal
          isOpen
          onClose={() => setModalShipmentId(null)}
          shipmentLabel={t('order.shipments.shipment_label', {
            index: modalShipmentIndex + 1,
          })}
          shipment={modalShipment}
          currency={currency}
        />
      )}
    </div>
  );
}
